import { useBrowseSession, useBrowsePosition } from '../../features/browse/useBrowseList';
import { useExpandedText } from '../../features/notifications/useExpandedText';
import baseline from '../../app-ui/baseline';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { FiBell, FiCheck, FiRefreshCw } from 'react-icons/fi';
import PageLayout from '../../components/layout/PageLayout';
import { AccountBoundary, SettingsHeader, useSettings } from '../../features/settings/shared';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useError } from '../../contexts/ErrorContext';
import {
  translate,
  notificationGroups,
  unreadCount,
  webDestination,
} from '../../features/notifications/model';
import { notificationDestination } from '../../features/notifications/nativeModel';
import { useNotifications } from '../../features/notifications/useNotifications';
import NotificationItem from './NotificationItem';
import '../../features/notifications/Notifications.css';
export default function Notifications() {
  return <AccountBoundary component={Boundary} />;
}
function Boundary({ user }) {
  const memory = useBrowseSession('notifications');
  return user ? (
    <List key={`${memory.location.key}:${memory.entry.version}`} user={user} {...memory} />
  ) : (
    <Navigate replace to="/login?redirectUrl=%2Fnotifications" />
  );
}
function List({ user, entry, returning }) {
  const { locale } = useWebLocale(),
    { style, dark } = useSettings(),
    { showError } = useError(),
    nav = useNavigate(),
    [filter, setFilter] = useState(entry.notificationFilter || 'all');
  const expanded = useExpandedText();
  const t = (key, vars) => translate(locale, key, vars),
    { query, busy, change } = useNotifications(user, () => showError(t('common.retryLater')));
  const items = query.data || [],
    filtered = filter === 'unread' ? items.filter((n) => !n.isRead) : items,
    groups = notificationGroups(filtered, locale);
  const position = useBrowsePosition({ entry, returning, ready: Boolean(query.data) });
  const selectedTab = useRef(null),
    focused = useRef(null),
    cancelled = useRef(false);
  useEffect(() => {
    const cancel = () => {
      cancelled.current = true;
    };
    for (const type of ['pointerdown', 'keydown', 'touchstart', 'wheel'])
      window.addEventListener(type, cancel, { capture: true, passive: true });
    return () => {
      for (const type of ['pointerdown', 'keydown', 'touchstart', 'wheel'])
        window.removeEventListener(type, cancel, true);
    };
  }, []);
  useEffect(() => {
    if (
      !returning ||
      !query.data ||
      !entry.current() ||
      !entry.notificationOrigin ||
      cancelled.current
    )
      return;
    // A successful read can remove the origin from the unread list, including after POP.
    if (focused.current?.isConnected) return;
    const origin = entry.notificationOrigin;
    const target =
      filtered.find((item) => item.id === origin.id) ||
      filtered[Math.min(origin.index, filtered.length - 1)];
    const nodes = position.ref.current?.querySelectorAll('[data-notification-id]') || [];
    const button = Array.from(nodes).find(
      (node) => node.dataset.notificationId === String(target?.id),
    );
    focused.current = button || selectedTab.current;
    focused.current?.focus({ preventScroll: true });
  }, [returning, query.data, entry, filtered, position.ref]);
  const chooseFilter = (value) => {
    if (entry.current()) entry.notificationFilter = value;
    setFilter(value);
  };
  const open = (item) => {
    if (busy) return;
    if (entry.current()) {
      entry.y = window.scrollY;
      entry.notificationOrigin = {
        id: item.id,
        index: filtered.findIndex((n) => n.id === item.id),
      };
    }
    if (!item.isRead) void change('read', item.id);
    const to = webDestination(notificationDestination(item));
    if (to) nav(to);
  };
  return (
    <PageLayout paddingTop={0}>
      <div
        {...position}
        className={`pp-settings pp-notifications${expanded ? ' is-expanded' : ''}`}
        style={{
          ...style,
          '--notification-body': baseline.tokens[dark ? 'darkColors' : 'lightColors'].reviewBody,
          '--notification-mutedSurface':
            baseline.tokens[dark ? 'darkColors' : 'lightColors'].surfaceMuted,
        }}
      >
        <SettingsHeader title={t('common.notifications')} />
        <div className="pp-notification-controls">
          <div
            role="tablist"
            aria-label={t('common.notifications')}
            className="pp-notification-filter"
          >
            {['all', 'unread'].map((value) => (
              <button
                role="tab"
                aria-selected={value === filter}
                key={value}
                ref={value === filter ? selectedTab : undefined}
                onClick={() => chooseFilter(value)}
              >
                {t(value === 'all' ? 'notifications.filterAll' : 'notifications.filterUnread')}
              </button>
            ))}
          </div>
          <div className="pp-notification-tools">
            {unreadCount(items) > 0 && (
              <button
                disabled={busy}
                onClick={() => change('all')}
                aria-label={t('notifications.markAllReadAccessibility')}
              >
                <FiCheck size={18} />
                {t('notifications.markAllRead')}
              </button>
            )}
            <button
              disabled={busy || query.isFetching}
              onClick={() => query.refetch({ cancelRefetch: false })}
              aria-label={locale === 'en' ? 'Refresh' : '새로고침'}
            >
              <FiRefreshCw size={18} />
            </button>
          </div>
        </div>
        {query.isPending && <p role="status">{t('common.loading')}</p>}
        {groups.map((group) => (
          <section className="pp-notification-group" key={group.key}>
            <h2>{group.label}</h2>
            <div>
              {group.items.map((item) => (
                <NotificationItem
                  key={item.id}
                  notification={item}
                  locale={locale}
                  busy={busy}
                  onPress={() => open(item)}
                  onDelete={() => change('delete', item.id)}
                />
              ))}
            </div>
          </section>
        ))}
        {query.isError && (
          <div role="alert">
            <p>{t('notifications.loadError')}</p>
            <button
              disabled={busy || query.isFetching}
              onClick={() => query.refetch({ cancelRefetch: false })}
            >
              {t('common.retry')}
            </button>
          </div>
        )}
        {!query.isPending && !query.isError && !filtered.length && (
          <div className="pp-notification-empty">
            {items.length ? <FiCheck size={28} /> : <FiBell size={28} />}
            <strong>{t(items.length ? 'notifications.noUnread' : 'notifications.empty')}</strong>
            <p>{t(items.length ? 'notifications.noUnreadBody' : 'notifications.emptyBody')}</p>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
